import { roundValue } from '../../../utils/math';
import { scaleZScore, zScore } from '../../../utils/scoring';
import {
  EZScorePreference,
  IAggregate,
  IComputedAggregate,
  ITest,
} from '../../types';

/**
 * @description calculates statistics given a test result and a comparison population
 * @param test
 * @param aggregateMetrics
 */
export const calculateStatistics = (
  test: ITest,
  aggregateMetrics: IComputedAggregate['data'],
  scale: EZScorePreference
): IComputedAggregate['data'] => {
  const metricKeys = Object.keys(aggregateMetrics ?? {});
  for (const metricKey of metricKeys) {
    /// check that this is an metric
    if (test?.[metricKey]?.value) {
      const { mean, populationSd } = aggregateMetrics[metricKey];
      const { value, direction, units } = test[metricKey];
      const zscore = zScore(value, mean, populationSd, direction);
      const change = value - mean;

      const changePercentage = percentChange(units, value, mean);
      aggregateMetrics[metricKey].change = changePercentage;
      aggregateMetrics[metricKey].zscore =
        scale === EZScorePreference.Scaled
          ? scaleZScore(zscore)
          : roundValue(zscore, 2);
    }
  }
  return aggregateMetrics;
};

/**
 * @description calculates raw z score
 * @param value
 * @param population
 */
export const calculateRawZScore = (
  value: number,
  population: IAggregate
): number => {
  if (population.populationSd === 0 || isNaN(population.populationSd)) {
    return null;
  }
  const score =
    (Math.abs(value) - Math.abs(population.mean)) /
    Math.abs(population.populationSd);
  if (score > 4) {
    return 4;
  }
  if (score < -4) {
    return -4;
  }
  return roundValue(score, 2);
};

/**
 * @description determine the difference between the actual change and the smallest meaningful change
 * @param change
 * @param smc
 * @returns
 */
const changeMagnitude = (change: number, smc: number): number => {
  return Math.abs(change) - Math.abs(smc);
};

/**
 * @description calculates percent change
 * @param units
 * @param previous
 * @param current
 * @returns
 */
export const percentChange = (
  units: string,
  current: number,
  previous: number
): number => {
  const change = current - previous;
  return units === '%' ? change : (change / previous) * 100;
};
